import {
	ApiError,
	HttpRequest,
} from '../stores/types/types';
import {
	buildSyncUrl,
	getSyncHeaders,
	handleResponseError,
} from './utils';

export type AutoplayData = {
	id: number;
	durationLeft: number;
	pickedUp: boolean;
}

type ApiReturn = AutoplayData | ApiError;

const isApiError = (json: ApiReturn): json is ApiError => (
	(json as ApiError).error !== undefined
);

const getShotTimerOrNull = async (
	response: Response,
): Promise<AutoplayData | null> => {
	const json: ApiReturn = await response.json();
	const hasErrorMessage = isApiError(json);

	if (!response.ok || hasErrorMessage) {
		if (hasErrorMessage) {
			// eslint-disable-next-line no-console
			console.info(`Autoplay: ${json.error}`);
		}

		return null;
	}

	return json;
};

export const GetAutoplayTimer = async (
	token: string,
	shotId: number,
	roomId: number,
): Promise<AutoplayData | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/autoplay/${shotId}`);
		const response = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});

		return getShotTimerOrNull(response);
	} catch (error) {
		return handleResponseError({
			errorMessage: error as string,
		});
	}
};

export const CreateOrUpdateAutoplayTimer = async (
	token: string,
	shotId: number,
	durationLeft: number,
	roomId: number,
): Promise<AutoplayData | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/autoplay/${shotId}`);
		const response = await fetch(url, {
			method: HttpRequest.Post,
			headers: getSyncHeaders(token),
			body: JSON.stringify({
				durationLeft,
			}),
		});

		return getShotTimerOrNull(response);
	} catch (error) {
		return handleResponseError({
			errorMessage: error as string,
		});
	}
};

export const DeleteAutoplayTimer = async (
	token: string,
	shotId: number,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/autoplay/${shotId}`);
		const response = await fetch(url, {
			method: HttpRequest.Delete,
			headers: getSyncHeaders(token),
		});

		if (!response.ok) {
			const json: ApiError = await response.json();
			const hasErrorMessage = isApiError(json);

			if (hasErrorMessage) {
				// eslint-disable-next-line no-console
				console.info(`Autoplay: ${json.error}`);
			}

			return false;
		}

		return true;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);

		return false;
	}
};

export const DeleteAllTimersForRoom = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`/v1/timers/rooms/${roomId}`);
		const response = await fetch(url, {
			method: HttpRequest.Delete,
			headers: getSyncHeaders(token),
		});

		if (response.ok) {
			return true;
		}

		const json: ApiError = await response.json();
		const hasErrorMessage = isApiError(json);

		if (hasErrorMessage) {
			// eslint-disable-next-line no-console
			console.info(`Autoplay: ${json.error}`);
		}

	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);
	}

	return false;
}